import { createContext, Dispatch, SetStateAction } from 'react';

// Channel types - matching EDMS ChannelType enum
export type ChannelType = 'Email' | 'Teams' | 'Grouped';

export interface Channel {
  id: string;
  name: string;
  type: ChannelType;
  icon: string;
}

export interface SignalTriggerSettings {
  triggerType: 'minimum' | 'maximum' | 'range';
  threshold: string;
  thresholdMax: string;
  triggerLogicType: 'percentage' | 'period' | 'spike';
  percentageThreshold?: string;
  evaluationWindow?: string;
  periodMinutes?: string;
  numberOfSpikes?: string;
  cooldownPeriod?: string;
  advancedSettingsExpanded?: boolean;
}

export interface SignalMeta {
  category: string;
  unit: string;
}

export interface AlarmFormData {
  // Step 1: Alarm Configuration
  name: string;
  machines: string[];
  signals: string[];
  category: string;
  signalUnit: string;

  // Step 4: Trigger
  threshold: string;
  operation: 'greater' | 'less';

  // Step 4: Conditions
  triggerType: 'minimum' | 'maximum' | 'range';
  thresholdMax: string;

  // Step 3: Channels
  selectedChannels: string[];
  channelSearchQuery: string;
  channelTypeFilters: ChannelType[]; // Active channel type filters

  // Step 4: Trigger logic
  triggerLogicType: 'percentage' | 'period' | 'spike';
  percentageThreshold?: string;
  evaluationWindow?: string;
  periodMinutes?: string;
  numberOfSpikes?: string;
  cooldownPeriod?: string;

  // Multi-signal only: per-signal trigger/logic settings keyed by signal name
  multiSignalSettings?: Record<string, SignalTriggerSettings>;

  // Category and unit of each selected signal, keyed by signal name. category
  // and signalUnit above only describe signals[0], so a grouped alert needs
  // these to encode each member under its own semantics.
  signalMeta?: Record<string, SignalMeta>;
}

export interface AddAlarmDialogContextValue {
  // Form data
  formData: AlarmFormData;
  setFormData: Dispatch<SetStateAction<AlarmFormData>>;
  updateFormData: (updates: Partial<AlarmFormData>) => void;

  // Step management
  currentStep: number;
  setCurrentStep: (step: number) => void;

  // UI state
  advancedSettingsExpanded: boolean;
  setAdvancedSettingsExpanded: (expanded: boolean) => void;

  // Machine data
  selectedMachineIds: string[];
  availableMachines: Array<{ id: string; displayName: string }>;

  // Edit mode
  isEditMode: boolean;
  editingAlarmId: string | null;
}

export const AddAlarmDialogContext = createContext<
  AddAlarmDialogContextValue | undefined
>(undefined);
